import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { TitleText, BodyText, Button, Card } from '../ui';
import { AvatarPhoto } from '../ui/AvatarPhoto';
import { cn } from '../../utils/cn';
import type { PatientData, DietitianProfile, ReferralSubmissionResult } from '../../types/referral';
import { REFERRAL_STORAGE } from '../../constants/storage';
import { getHealthConditionLabels } from '../../constants/healthConditions';

interface ReferralConfirmationProps {
  patientData: Partial<PatientData>;
  selectedDietitian: DietitianProfile | null;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  submissionResult?: ReferralSubmissionResult | null;
}

/**
 * Step 3: Review & Confirmation
 * Component shell for reviewing all referral details and submitting
 * Will be fully implemented in Phase 4
 */
export const ReferralConfirmation: React.FC<ReferralConfirmationProps> = ({
  patientData,
  selectedDietitian,
  onSubmit,
  onBack,
  isSubmitting = false,
  submissionResult,
}) => {
  const { isDark } = useTheme();

  // Show success state if submission was successful
  if (submissionResult?.status === 'success') {
    const handleNewReferral = () => {
      // Clear locally stored form data
      localStorage.removeItem(REFERRAL_STORAGE.WORKFLOW);
      // Reload page to redirect to first step
      window.location.reload();
    };

    const handleGoToDashboard = () => {
      // Navigate to dashboard - would use router in real app
      window.location.href = '/dashboard';
    };

    return (
      <div className="space-y-8">
        {/* Success Header */}
        <div className="text-center">
          <TitleText level={1} className="mb-4">
            Referral Submitted Successfully!
          </TitleText>
          <BodyText variant="secondary" size="lg">
            Your patient referral has been processed and saved to your records.
          </BodyText>
        </div>

        {/* Referral Details Card */}
        <Card className={cn(
          'p-6',
          isDark ? 'bg-secondary-800' : 'bg-white'
        )}>
          <div className="flex items-center mb-6">
            <span className="text-2xl mr-3">📄</span>
            <div>
              <TitleText level={4} className="mb-1">
                Referral Details
              </TitleText>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <BodyText variant="secondary" size="sm" className="mb-1 font-medium">
                Patient
              </BodyText>
              <BodyText variant="primary" className="break-words">
                {submissionResult.patientName || patientData.patientName || 'N/A'}
              </BodyText>
            </div>

            <div>
              <BodyText variant="secondary" size="sm" className="mb-1 font-medium">
                Referred to
              </BodyText>
              <BodyText variant="primary" className="break-words">
                {submissionResult.dietitianName || selectedDietitian?.name || 'N/A'}
              </BodyText>
            </div>

          </div>
        </Card>

        {/* What Happens Next */}
        <Card className={cn(
          'p-6',
          isDark ? 'bg-secondary-800' : 'bg-white'
        )}>
          <div className="flex items-center mb-6">
            <span className="text-2xl mr-3">📋</span>
            <TitleText level={4}>
              What Happens Next?
            </TitleText>
          </div>

          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1',
                'bg-primary-100 text-primary-700'
              )}>
                1
              </div>
              <div>
                <BodyText variant="primary" className="font-medium mb-1">
                  Dietitian Notification
                </BodyText>
                <BodyText variant="secondary" size="sm" className="break-words">
                  {submissionResult.dietitianName || selectedDietitian?.name} will be notified of the referral and will contact the patient within 2 business days.
                </BodyText>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1',
                'bg-primary-100 text-primary-700'
              )}>
                2
              </div>
              <div>
                <BodyText variant="primary" className="font-medium mb-1">
                  Patient Contact
                </BodyText>
                <BodyText variant="secondary" size="sm">
                  The patient will be contacted directly to schedule their first appointment.
                </BodyText>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1',
                'bg-primary-100 text-primary-700'
              )}>
                3
              </div>
              <div>
                <BodyText variant="primary" className="font-medium mb-1">
                  Progress Updates
                </BodyText>
                <BodyText variant="secondary" size="sm">
                  You'll receive updates on the patient's progress and treatment outcomes.
                </BodyText>
              </div>
            </div>
          </div>
        </Card>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Download PDF */}
          {/* <Card className={cn(
            'p-6 text-center cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1',
            isDark ? 'bg-secondary-800 hover:bg-secondary-750' : 'bg-white hover:bg-secondary-50'
          )} onClick={handleDownloadPdf}>
            <TitleText level={5} className="mb-2">
              Download PDF
            </TitleText>
            <BodyText variant="secondary" size="sm" className="mb-4">
              Get a PDF copy of the referral document
            </BodyText>
            <Button variant="outline" size="sm" className="w-full">
              Download PDF
            </Button>
          </Card> */}

          {/* Dashboard */}
          <Card className={cn(
            'p-6 text-center cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1',
            isDark ? 'bg-secondary-800 hover:bg-secondary-750' : 'bg-white hover:bg-secondary-50'
          )} onClick={handleGoToDashboard}>
            <TitleText level={5} className="mb-2">
              Dashboard
            </TitleText>
            <BodyText variant="secondary" size="sm" className="mb-4">
              View all your referrals
            </BodyText>
            <Button variant="outline" size="sm" className="w-full">
              Go to Dashboard
            </Button>
          </Card>

          {/* New Referral */}
          <Card className={cn(
            'p-6 text-center cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1',
            isDark ? 'bg-secondary-800 hover:bg-secondary-750' : 'bg-white hover:bg-secondary-50'
          )} onClick={handleNewReferral}>
            <TitleText level={5} className="mb-2">
              New Referral
            </TitleText>
            <BodyText variant="secondary" size="sm" className="mb-4">
              Refer another to our network
            </BodyText>
            <Button variant="primary" size="sm" className="w-full">
              Refer Patient
            </Button>
          </Card>
        </div>

        {/* Support information */}
        <div className="text-center">
          <p className={cn(
            'text-sm',
            isDark ? 'text-secondary-400' : 'text-secondary-600'
          )}>
            Need help? Contact our support team at{' '}
            <a
              href="mailto:admin@bitewise.com.au"
              className="text-blue-600 hover:underline"
            >
              admin@bitewise.com.au
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Show error state if submission failed
  if (submissionResult?.status === 'error') {
    return (
      <div className="space-y-6">
        <Card className={cn(
          'p-6 border-l-4 border-error-500',
          isDark ? 'bg-error-900/20' : 'bg-error-50'
        )}>
          <TitleText level={4} className="mb-2">
            Submission Failed
          </TitleText>
          <BodyText variant="secondary" className="mb-4">
            {submissionResult.message}
          </BodyText>
          <div className="flex space-x-4">
            <Button onClick={onSubmit}>
              Try Again
            </Button>
            <Button variant="outline" onClick={onBack}>
              Go Back
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main review and confirmation interface
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <TitleText level={2} className="mb-2">
          Review & Submit Referral
        </TitleText>
        <BodyText variant="secondary">
          Please review all details before submitting the referral.
        </BodyText>
      </div>

      {/* Review Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Patient Summary */}
        <Card className={cn(
          'p-6',
          isDark ? 'bg-secondary-800' : 'bg-white'
        )}>
          <TitleText level={4} className="mb-4">
            Patient Information
          </TitleText>

          <div className="space-y-3">
            <div>
              <BodyText variant="secondary" size="sm" className="mb-1">
                Name:
              </BodyText>
              <BodyText variant="primary" className="break-words">
                {patientData.patientName || 'Not provided'}
              </BodyText>
            </div>

            <div>
              <BodyText variant="secondary" size="sm" className="mb-1">
                Contact:
              </BodyText>
              <BodyText variant="primary" className="break-all">
                {patientData.email || 'Not provided'}
              </BodyText>
            </div>

            <div>
              <BodyText variant="secondary" size="sm" className="mb-1">
                Health Conditions:
              </BodyText>
              <div className="flex flex-wrap gap-2">
                {patientData.conditions && patientData.conditions.length > 0 ? (
                  getHealthConditionLabels(patientData.conditions).map((label, index) => (
                    <span
                      key={`${patientData.conditions![index]}-${index}`}
                      className={cn(
                        'px-2 py-1 rounded text-xs',
                        isDark
                          ? 'bg-secondary-700 text-secondary-300'
                          : 'bg-secondary-100 text-secondary-700'
                      )}
                    >
                      {label}
                    </span>
                  ))
                ) : (
                  <BodyText variant="muted" size="sm">None selected</BodyText>
                )}
              </div>
            </div>

            {patientData.additionalNotes && (
              <div>
                <BodyText variant="secondary" size="sm" className="mb-1">
                  Additional Notes:
                </BodyText>
                <BodyText variant="primary" className="break-words whitespace-pre-wrap">
                  {patientData.additionalNotes}
                </BodyText>
              </div>
            )}
          </div>
        </Card>

        {/* Dietitian Summary */}
        <Card className={cn(
          'p-6',
          isDark ? 'bg-secondary-800' : 'bg-white'
        )}>
          <TitleText level={4} className="mb-4">
            Selected Dietitian
          </TitleText>

          {selectedDietitian ? (
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <AvatarPhoto
                  src={selectedDietitian.avatar}
                  firstName={selectedDietitian.name.split(' ')[0]}
                  lastName={selectedDietitian.name.split(' ').slice(1).join(' ')}
                  size="md"
                />

                <div>
                  <TitleText level={5} className="mb-1 break-words">
                    {selectedDietitian.name}
                  </TitleText>
                  <BodyText variant="secondary" size="sm" className="break-words">
                    {selectedDietitian.credentials}
                  </BodyText>
                </div>
              </div>

              <div>
                <BodyText variant="secondary" size="sm" className="mb-1">
                  Specialties:
                </BodyText>
                <div className="flex flex-wrap gap-2">
                  {selectedDietitian.specialties.map((specialty) => (
                    <span
                      key={specialty}
                      className={cn(
                        'px-2 py-1 rounded text-xs',
                        isDark
                          ? 'bg-primary-900 text-primary-300'
                          : 'bg-primary-100 text-primary-700'
                      )}
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <BodyText variant="secondary" size="sm" className="mb-1">
                  Location:
                </BodyText>
                <BodyText variant="primary" className="break-words">
                  {selectedDietitian.location}
                </BodyText>
              </div>

            </div>
          ) : (
            <BodyText variant="muted">
              No dietitian selected
            </BodyText>
          )}
        </Card>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back: Select Dietitian
        </Button>

        <Button
          onClick={onSubmit}
          disabled={isSubmitting || !selectedDietitian || !patientData.patientName}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Referral'}
        </Button>
      </div>

      {/* Validation Messages */}
      {(!selectedDietitian || !patientData.patientName) && (
        <Card className={cn(
          'p-4 border-l-4 border-warning-500',
          isDark ? 'bg-warning-900/20' : 'bg-warning-50'
        )}>
          <BodyText variant="primary" className="font-medium mb-2">
            Please complete the following before submitting:
          </BodyText>
          <ul className="space-y-1">
            {!patientData.patientName && (
              <li>
                <BodyText variant="secondary" size="sm">• Complete patient information</BodyText>
              </li>
            )}
            {!selectedDietitian && (
              <li>
                <BodyText variant="secondary" size="sm">• Select a dietitian</BodyText>
              </li>
            )}
          </ul>
        </Card>
      )}
    </div>
  );
};